// Engine B (server-side SDMS Formula Engine) — sheet-input overlay for the on-EDIT recompute.
//
// Kept in its own module (no React / Univer / network) so the band-walk + overlay contract can be
// unit-tested against a plain xlsx buffer. Used only for templates whose SDMS Formula Setup has
// runnable (Fixed) bindings; the caller gates on `sdmsHasFormulas`, so for the common (unbound)
// template none of this runs and the sheet keeps its own Univer formulas.

import { calcRowKey, SHEET_RAW_RESULT_KEY } from './sdmsEngineCompute';

/** The TEXT an ExcelJS cell shows, for the band walks that read the sheet back (the Engine B input overlay, its
 *  write-back, the dilution IsReport rule). A FORMULA cell is its cached result — and BLANK when it has none: the
 *  old `String(c.result ?? c.value ?? …)` fell through to the formula OBJECT and read "[object Object]". Calcium
 *  8067's NumericResult is `=IF(F7="","",F7*8.016)` (no cached result while F is empty), so Engine B was sent
 *  numericResult "[object Object]", echoed it as the Result, wrote that into every Result cell, and the write — an
 *  edit — re-triggered the recompute (user report 2026-10-10; OG reads the formula's value). Rich text is its text,
 *  an error cell its error text. */
export function sheetCellText(c: import('exceljs').Cell | undefined | null): string {
  if (!c) return '';
  const scalar = (x: unknown): string => (x == null || typeof x === 'object' && !(x instanceof Date) ? '' : String(x)).trim();
  const v = c.value as unknown;
  if (v != null && typeof v === 'object' && !(v instanceof Date)) {
    const o = v as Record<string, unknown>;
    if ('formula' in o || 'sharedFormula' in o) {
      const r = c.result as unknown ?? o.result;
      if (r != null && typeof r === 'object' && 'error' in (r as object)) return String((r as { error: unknown }).error).trim();
      return scalar(r);
    }
    if (Array.isArray(o.richText)) return (o.richText as Array<{ text?: string }>).map(t => t.text ?? '').join('').trim();
    if ('error' in o) return String(o.error).trim();
    if ('text' in o) return scalar(o.text);       // hyperlink
    if ('result' in o) return scalar(o.result);
    return '';
  }
  return scalar(v);
}

/** A date cell as the canonical `MM/DD/YYYY HH:mm` text the engine reads (SdmsDateText on the server): a Date
 *  (ExcelJS, UTC axis) or an Excel serial, either as the value or as a formula's cached result (HAA BF8
 *  `IF(D8="THAA5",BF7,serial)`); text passes through. Empty when the cell holds nothing. */
export function sheetCellDateText(c: import('exceljs').Cell | undefined | null): string {
  if (!c) return '';
  const v = c.value as unknown;
  const raw = v != null && typeof v === 'object' && !(v instanceof Date) && ('formula' in v || 'sharedFormula' in v || 'result' in v)
    ? (c.result as unknown ?? (v as { result?: unknown }).result) : v;
  const d = raw instanceof Date ? raw
    : typeof raw === 'number' && Number.isFinite(raw) ? new Date(Math.round((raw - 25_569) * 86_400_000)) : null;
  if (!d || Number.isNaN(d.getTime())) return sheetCellText(c);
  const p2 = (n: number) => String(n).padStart(2, '0');
  return `${p2(d.getUTCMonth() + 1)}/${p2(d.getUTCDate())}/${d.getUTCFullYear()} ${p2(d.getUTCHours())}:${p2(d.getUTCMinutes())}`;
}

/** Sheet names that are NOT result-detail bands (config / summary / calibration / …) — skipped by
 *  every band-walk (the overlay here + the write-backs in SdmsResultEntryPage). Single source of
 *  truth so the read and write walks can never diverge on which sheets carry results. */
/** Is this worksheet a RESULT sheet (one the data band lives on)? By NAME for the sub-sheets
 *  (NON_RESULT_SHEETS) — except that the FIRST worksheet is always the template's main sheet,
 *  whatever it is called: THM 8112 names its main sheet "Summary", so every name-only check
 *  skipped its whole band (the dilution IsReport rule, the engine overlay / write-back, the
 *  dropdown overlays, the change list). Model-1 templates put the per-run sheets first, never a
 *  sub-sheet, so index 0 is safe there too. (parity, 2026-09-23) */
export function isResultSheet(name: string, index: number): boolean {
  return index === 0 || !NON_RESULT_SHEETS.has(name.toUpperCase());
}

export const NON_RESULT_SHEETS = new Set([
  'SEQUENCE', 'SUMMARY', 'ISTD', 'REAGENTS', 'INSTRUMENTS', 'CALIBRATION',
  'RAW DATA', 'REGRESSION CURVES', 'PARAMETER-ORIENTED SUMMARY', 'SAMPLE-ORIENTED SUMMARY', 'QUALIFIER',
]);

/** The fields Engine B writes back into the sheet (writeEngineResultsToSheet + its harness twin). */
export const ENGINE_OUTPUT_FIELD_KEYS: ReadonlySet<string> = new Set([
  'RESULT', '%RECOVERY', 'RPD', 'QCSTATUS', 'ANALYZEDDATE', 'DUPSOURCERESULT', 'SPIKESOURCERESULT',
]);

/** The Field→cell map with every entry dropped whose template cell is bound to a DIFFERENT field.
 *  DataParsing can put two fields on one Position — Total Residual Chlorine 8078 AM8 is both
 *  DupSourceResult and Analyst (AL8 both DupSource and AnalysisDate). OG writes engine outputs into
 *  its data table and re-binds the sheet, so the cell shows the field the TEMPLATE binds there (the
 *  Analyst); writing into it by position replaced the Analyst with the Dup source result "0" and
 *  locked the Analyst column as an engine output (2026-09-26). A cell holding a formula, a constant,
 *  nothing, or FIELD() of the same field keeps its entry. Positions are on the template's first
 *  (main) sheet, as the map's bare A1 refs are. Only the fields in `onlyFields` are checked (the
 *  engine OUTPUTS — the same map also locates the engine's INPUTS, which stay as they are).
 *  `templateBuf` null → the map unchanged. */
export async function ownFieldCells(
  fieldCells: Record<string, string>, templateBuf: ArrayBuffer | null | undefined,
  onlyFields: ReadonlySet<string> = ENGINE_OUTPUT_FIELD_KEYS,
): Promise<Record<string, string>> {
  if (!templateBuf) return fieldCells;
  try {
    const ExcelJS = (await import('exceljs')).default;
    const wb = new ExcelJS.Workbook();
    await wb.xlsx.load(templateBuf);
    const ws = wb.worksheets[0];
    if (!ws) return fieldCells;
    const norm = (s: string) => s.trim().toUpperCase().replace(/^\[(.+)\]$/, '$1');
    const out: Record<string, string> = {};
    for (const [field, a1] of Object.entries(fieldCells)) {
      if (!onlyFields.has(norm(field))) { out[field] = a1; continue; }
      const ref = String(a1 ?? '').replace(/^.*!/, '').replace(/\$/g, '').toUpperCase();
      if (!/^[A-Z]+\d+$/.test(ref)) { out[field] = a1; continue; }
      const v = ws.getCell(ref).value as { formula?: string; sharedFormula?: string } | null;
      const f = v && typeof v === 'object' ? (v.formula ?? v.sharedFormula ?? '') : '';
      const bound = /^\s*=?\s*FIELD\(\s*"([^"]+)"\s*\)\s*$/i.exec(f)?.[1];
      if (bound && norm(bound) !== norm(field)) continue;   // the template shows another field here
      // QCStatus is never stamped over the template's OWN formula: OG keeps the engine's QCStatus in its tables
      // ("QCStatus is not a sheet cell, so it is not stamped" — QCBatchReportingRecalc.cs) and its re-bind restores
      // the template formula, so the sheet shows what that formula computes. THM 8112 AO8 is an IF(...) of IS %
      // and the CCV1 IS verification; writing the engine's "Fail" over it showed Fail on the ISTD row where OG
      // shows Pass (QB26101302_001, 2026-10-13). A FIELD("QCSTATUS") cell or an empty one is still written.
      if (norm(field) === 'QCSTATUS' && f && !bound) continue;
      out[field] = a1;
    }
    // An engine OUTPUT the template shows with FIELD("…") but DataParsing never positions: OG writes the engine's
    // values into its data table and re-binds the sheet, so every FIELD() cell of that name shows them — Calcium
    // 8067 has FIELD("DUPSOURCERESULT") in AG7 and FIELD("SPIKESOURCERESULT") in AI7 with no DataParsing row, and
    // they stayed blank here while OG showed the parent sample's result (user report 2026-10-10). Only a field
    // ABSENT from the map is added (one dropped above for a conflict stays dropped); the topmost such cell (the
    // band's first row, where every other entry of the map points) is used.
    const known = new Set(Object.keys(fieldCells).map(norm));
    const found = new Map<string, { row: number; a1: string }>();
    ws.eachRow({ includeEmpty: false }, (row, r) => row.eachCell({ includeEmpty: false }, (cell) => {
      const v = cell.value as { formula?: string; sharedFormula?: string } | null;
      const f = v && typeof v === 'object' ? (v.formula ?? v.sharedFormula ?? '') : '';
      const name = /^\s*=?\s*FIELD\(\s*"([^"]+)"\s*\)\s*$/i.exec(f)?.[1];
      if (!name) return;
      const key = norm(name);
      if (!onlyFields.has(key) || known.has(key)) return;
      const prev = found.get(key);
      if (!prev || r < prev.row) found.set(key, { row: r, a1: cell.address });
    }));
    for (const [key, { a1 }] of found) out[key] = a1;
    return out;
  } catch (e) {
    console.warn('[SDMS] field-cell ownership check skipped', e);
    return fieldCells;
  }
}

/** Delimiter for the "edited cell" set key (`sheetName<SEP>row0`). NUL can never appear in a sheet
 *  name, so there is no ambiguity even when a name contains spaces (mirrors this file-family's
 *  existing `ROW_KEY` convention). Built via fromCharCode so the source stays plain ASCII and the
 *  writer (SdmsResultEntryPage.onSheetCellEdited) and this reader use the exact same byte. */
export const EDIT_KEY_SEP = String.fromCharCode(0);

/** On-edit Engine B input overlay. Reads the analyst's CURRENT sheet inputs — the entry
 *  result (NumericResult, or Result for entry-in-Result templates), DF, SpikeAmount — per
 *  result-band row and overlays them onto the load-time merge records, so the recompute reflects
 *  the edit (the load-time records alone would ignore it). Also returns the SysSampleCodes of the
 *  band rows the caller flagged as edited (via `editedCoords`, keyed `sheetName<SEP>row0`), so the
 *  host can target `recompute-sample-group`. Band-walk mirrors writeEngineResultsToSheet; keyed by
 *  (SysSampleCode|Parameter|RunNo). Never mutates the input records (returns clones). */
export async function overlaySheetInputs(
  buf: ArrayBuffer,
  fieldCells: Record<string, string>,
  records: readonly Record<string, unknown>[],
  editedCoords: ReadonlySet<string>,
  /** `buf` already parsed — the caller's one parse shared with the result write-back (speed, 2026-09-30). */
  parsed?: import('exceljs').Workbook,
): Promise<{ records: Record<string, unknown>[]; editedSamples: Set<string> }> {
  const editedSamples = new Set<string>();
  const parse = (a1?: string) => {
    const m = /^([A-Z]+)(\d+)$/.exec((a1 ?? '').toUpperCase());
    if (!m) return null;
    let col = 0; for (const ch of m[1]!) col = col * 26 + (ch.charCodeAt(0) - 64);
    return { col, row: parseInt(m[2]!, 10) };   // 1-based
  };
  const par = parse(fieldCells.PARAMETER);
  if (!par) return { records: records.map(r => ({ ...r })), editedSamples };
  const sysP = parse(fieldCells.SYSSAMPLECODE);
  const runP = parse(fieldCells.RUNNO);
  // The measured input: NumericResult, else RawResult, else Result. RawResult comes BEFORE Result: on a
  // template with no NumericResult cell whose import fills RawResult (PFAS_533_Waters 8114, Anions 1014)
  // Result is the engine's OUTPUT, so reading it fed the engine an empty value and Result stayed blank
  // after an import (G6 feedback 2026-10-15, Bug 5).
  const entryP = parse(fieldCells.NUMERICRESULT) ?? parse(fieldCells.RAWRESULT) ?? parse(fieldCells.RESULT);
  const dfP = parse(fieldCells.DF);
  const spkP = parse(fieldCells.SPIKEAMOUNT);
  // The engine's AnalyzedDate_MinBySample input — an import writes it to the SHEET, not the merge records, so it is
  // read back like the other inputs (HAA: undated rows kept the "now" default, user report 2026-10-16).
  const adP = parse(fieldCells.ANALYZEDDATE);
  // The sheet's own Result, when Result is a DIFFERENT cell from the entry: HAA 8075 enters NumericResult in J (the
  // Channel1 Result) and computes Result in O with a sheet formula (the dual-channel evaluation). OG's engine reads the
  // sheet's values, so %Recovery / RPD are worked from O; seeding Result with the entry worked them from J (LOQ1 Rec
  // 124 from 1.235 where Result is 1.1, user report 2026-10-16). A template whose engine computes Result still gets it
  // recomputed from NumericResult, so the seed only matters where the engine does not compute it.
  const resP = parse(fieldCells.RESULT);
  const resSeparate = !!resP && !!entryP && (resP.col !== entryP.col || resP.row !== entryP.row);
  // The instrument's own cell, when RawResult is a DIFFERENT cell from the entry: UV 254 (8058) imports the reading into
  // F (RAWRESULT) and shows NumericResult in G, which the import zero-fills to 0.000 (OG :22703) and the engine's
  // CalculateNumericResult then sets FROM RawResult. Feeding the engine G's 0 as its raw input left NumericResult and
  // Result at 0 after every import (user report 2026-10-21); OG's engine reads dtsample RawResult, i.e. F.
  const rawP = parse(fieldCells.RAWRESULT);
  const rawSeparate = !!rawP && !!entryP && (rawP.col !== entryP.col || rawP.row !== entryP.row);
  const startRow = par.row;
  const cellText = (ws: import('exceljs').Worksheet, row: number, col?: number): string => {
    if (!col) return '';
    return sheetCellText(ws.getRow(row).getCell(col));
  };
  let wb = parsed;
  if (!wb) {
    const ExcelJS = (await import('exceljs')).default;
    wb = new ExcelJS.Workbook();
    await wb.xlsx.load(buf);
  }
  const byKey = new Map<string, { entry: string; raw: string; df: string; spk: string; ad: string; res: string }>();
  for (const ws of wb.worksheets) {
    if (!isResultSheet(ws.name, wb.worksheets.indexOf(ws))) continue;
    for (let row = startRow; row <= ws.rowCount; row++) {
      const pName = cellText(ws, row, par.col);
      if (!pName) break;   // end of the detail band
      const sys = sysP ? cellText(ws, row, sysP.col) : ws.name;
      byKey.set(calcRowKey(sys, pName, runP ? cellText(ws, row, runP.col) : ''), {
        entry: entryP ? cellText(ws, row, entryP.col) : '',
        raw: rawSeparate ? cellText(ws, row, rawP!.col) : '',
        df: dfP ? cellText(ws, row, dfP.col) : '',
        spk: spkP ? cellText(ws, row, spkP.col) : '',
        ad: adP ? sheetCellDateText(ws.getRow(row).getCell(adP.col)) : '',
        res: resSeparate ? cellText(ws, row, resP!.col) : '',
      });
      if (sys && editedCoords.has(`${ws.name}${EDIT_KEY_SEP}${row - 1}`)) editedSamples.add(sys);
    }
  }
  const recKey = (rec: Record<string, unknown>): string => {
    const lo = new Map<string, unknown>();
    for (const k of Object.keys(rec)) lo.set(k.toLowerCase(), rec[k]);
    const pick = (...names: string[]): string => {
      for (const n of names) { const v = lo.get(n.toLowerCase()); if (v != null && String(v).trim() !== '') return String(v); }
      return '';
    };
    return calcRowKey(pick('SYSSamplecode', 'SysSampleCode'), pick('Parameter'), pick('RunNo'));
  };
  const out = records.map(r => {
    const sheet = byKey.get(recKey(r));
    const clone = { ...r };
    if (sheet) {
      // The entry value is the measured result; the server's BuildRow feeds it into
      // NumericResult / RawResult / Result alike, so overlay both record fields.
      if (sheet.entry !== '') { clone.NumericResult = sheet.entry; clone.RawResult = sheet.entry; clone.Result = sheet.entry; }
      if (sheet.res !== '') clone.Result = sheet.res;
      if (sheet.raw !== '') { clone.RawResult = sheet.raw; clone[SHEET_RAW_RESULT_KEY] = sheet.raw; }
      if (sheet.df !== '') clone.Dilution = sheet.df;
      if (sheet.spk !== '') clone.SpikeAmount = sheet.spk;
      if (sheet.ad !== '') clone.AnalyzedDate = sheet.ad;
    }
    return clone;
  });
  return { records: out, editedSamples };
}
